<template>
<div>
	<collapsible-panel
		:always-open="true"
		panel-size="sm"
		style="margin-top: -14px;">

		<template slot="panel">
			<h5>Invoicing Steps</h5>

			<panel-menu class="mb-2"
				:menu-items="panelTabs"
				:active-item="activeStep"
				v-on:itemClick="activateStep">
			</panel-menu>

			<div class="mt-3">
				<a v-if="invoiceSlug != 'new' && isLatestJobInvoice" href="#" v-on:click.prevent="deleteInvoice" class="text-danger">Delete Invoice...</a>
			</div>
		</template>
		
		<template slot="main">

			<div class="d-flex flex-row justify-content-between align-items-start">
				<div>
					<router-link to="/financial/invoices" class="mr-2">
						Invoices module
					</router-link>
					<span class="mr-2">/</span>
					<a :href="'/project/'+$store.project.slug+'/billing/invoices'" v-on:click.prevent="closeInvoice">
						Project invoices
					</a>
				</div>
				<i class="fa fa-times icon-button" title="Back to invoices" v-on:click="closeInvoice"></i>
			</div>

			<h2 v-if="invoiceSlug != 'new'">Invoice #{{$store.project.job_num}}-{{ invoice.invoice_num }} <template v-if="invoice.revision_num > 0">R{{invoice.revision_num}}</template></h2>
			<h2 v-else>New Invoice - #{{$store.project.job_num}}</h2>

			<div class="row" >
				<section v-if="activeStep == 'Draw'" class="col-lg-6">

					<table class="table table-sm table-striped">
						<tbody>
							<tr>
								<th>Holdback to Date</th>
								<td class="text-right">$ {{ invoiceTotals().holdback.total.toCurrency() }}</td>
							</tr>
							<tr>
								<th>Holdback Release to Date</th>
								<td class="text-right">$ {{ invoiceTotals().holdbackRelease.previous.toCurrency() }}</td>
							</tr>
						</tbody>
					</table>

					<field label="Holdback Release (current) $"
						col-class="col-md-7"
						:params="{type:'number',step:0.01}"
						:readonly="invoice.is_finalized == 1"
						key="holdbackRelease"
						v-model="invoice.holdback_release">
					</field>

					<h3>Schedule of Values</h3>

					<alert v-if="hasOverbilledTasks"
						:visible="overbilledTasksAlert"
						classes="alert-warning"
						icon="fa-warning"
						message="You have tasks that you are billing beyond 100% completion"
						v-on:close="overbilledTasksAlert = false">
					</alert>

					<table class="table table-sm">
						<thead>
							<tr>
								<th>Task</th>
								<th class="text-right">
									{{ $store.project.billing_progress_method == 'dollars' ? 'Total $' : 'Total Qty'}}
								</th>
								<th class="text-right">Total Prev. Requisition</th>
								<th>Compl. to Date ({{progressUnits}})</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(task,index) in mutateTasks(tasks,invoice)">
								<td>{{ task.item_number }} {{ task.description }}</td>
								<td class="text-right">
									{{ $store.project.billing_progress_method == 'dollars' ? '$ ' + task.totalValue : task.qty }}
								</td>
								<td class="text-right">{{ formatDrawProgressUnits(task) }}</td>
								<td class="text-right">
									<field v-if="!invoice.is_finalized || invoice.is_finalized == 0" 
										style="max-width: 120px;;min-width: 80px;" 
										:params="drawQtyFieldParams" 
										v-model="invoiceTasksQty[index]">
									</field>
									<span v-else>{{ formatDrawQtyDisplay(invoiceTasksQty[index]) }}</span>
								</td>
							</tr>
						</tbody>
					</table>

					<h3>Changes</h3>

					<alert v-if="hasOverbilledChanges"
						:visible="overbilledChangesAlert"
						classes="alert-warning"
						icon="fa-warning"
						message="You have changes that you are billing beyond 100% completion"
						v-on:close="overbilledChangesAlert = false">
					</alert>

					<table class="table table-sm">
						<thead>
							<tr>

								<th>Change Order #</th>
								<th class="text-right">{{ $store.project.billing_progress_method == 'dollars' ? 'Total $' : 'Total Qty'}}</th>
								<th class="text-right">Total Prev. Requisition</th>
								<th>Compl. to Date ({{ progressUnits }})</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(change,index) in mutateChangeOrders(changeOrders,invoice)">
								<td>{{ change.item_number }}
									<small v-if="change.is_cost_plus == 1 && change.quote_status != 'Complete, Approved'" class="text-warning">
										<i class="fa fa-exclamation"></i>
										(un-approved)
									</small>
								</td>
								<td class="text-right">{{ $store.project.billing_progress_method == 'dollars' ? '$ ' + change.totalValue : '1' }}</td>
								<td class="text-right">{{ formatDrawProgressUnits(change) }}</td>
								<td class="text-right">
									<field v-if="!invoice.is_finalized || invoice.is_finalized == 0" 
										style="max-width: 120px;min-width: 80px;" 
										:params="{type:'number',min:0,step:0.01}" 
										:readonly="change.is_cost_plus == 1 && change.quote_status != 'Complete, Approved'"
										v-model="invoicedChangesQty[index]">
									</field>
									<span v-else>{{ formatDrawQtyDisplay(invoicedChangesQty[index]) }}</span>
								</td>
							</tr>
						</tbody>
					</table>

					<div class="float-right">
						<!--
						<button class="btn btn-primary" v-on:click="saveInvoice(false)">
							<i class="fa fa-save"></i>
							Save
						</button>
						-->

						<button class="btn btn-light" v-on:click="activeStep = 'Finalizing'">
							Next Step
							<i class="fa fa-chevron-right"></i>
						</button>
					</div>
				</section>
				<section v-if="activeStep == 'Finalizing'" class="col-lg-6">
								
					<h3>Finalizing</h3>

					<field v-for="tax in salesTaxes"
						:label="'Charge '+tax.label"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'boolean'}"
						:readonly="invoice.is_finalized == 1"
						v-model="appliedTaxes[tax.label]"
						v-on:change="salesTaxToggle(tax.label)">
					</field>

					<field 
						class="mb-2"
						label="Invoice Date"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'date'}" 
						:readonly="invoice.is_finalized == 1"
						:invalid="$store.errorList.issue_date"
						key="invoiceDate"
						v-model="invoice.issue_date">
					</field>

					<field 
						class="mb-2"
						label="ATTN"
						col-class="col-lg-3 col-sm-4"
						:readonly="invoice.is_finalized == 1"
						v-model="invoice.attn">
					</field>

					<field 
						class="mb-2"
						label="Payment Terms"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'select',options:paymentTermsOptions}" 
						:invalid="$store.errorList.payment_terms"
						:readonly="invoice.is_finalized == 1"
						v-model="invoice.payment_terms">
					</field>

					<field 
						class="mb-2"
						label="Notes to Recipient"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'textarea'}" 
						:readonly="invoice.is_finalized == 1"
						v-model="invoice.customer_notes">
						<div class="text-muted">Appears on the invoice as a "Customer Notes" section</div>
					</field>

					<field 
						class="mb-2"
						label="Bottom Text"
						col-class="col-lg-3 col-sm-4"
						:params="{type:'richtext'}" 
						:readonly="invoice.is_finalized == 1"
						v-model="invoice.bottom_text">
						<div class="text-muted">Appears as fine print at the bottom of the invoice</div>
					</field>

					<div v-if="!invoice.is_finalized || invoice.is_finalized == 0" class="d-flex flex-row justify-content-between">
						<div>
							<button class="btn btn-light float-left" v-on:click="activeStep = 'Draw'">
								<i class="fa fa-chevron-left"></i> Previous Step
							</button>
						</div>

						<button class="btn btn-lg btn-success" v-on:click="saveInvoice(true)">
							<i class="fa fa-lock"></i>
							Finalize Invoice
						</button>
					</div>
				
				</section>
				<section v-if="activeStep == 'Delivery'" class="col-lg-6">

					<context-menu v-if="invoiceSlug && invoiceSlug != 'new'"
						class="float-right"
						:menu-items="contextMenuItems"
						v-on:itemClick="contextMenuClick">
					</context-menu>

					<invoice-delivery-section
						:invoice-details="invoice"
						:client-invoice-settings="clientInvoiceSettings"
						:has-attached-files="false"
						:customer="customer"
						type-name="Invoice"
						api-resource="JobInvoice"
						v-on:deFinalize="definalize">
					</invoice-delivery-section>

					<activity-log 
						:submitted-log-json="invoice.submitted_log_json"
						:seen-log-json="invoice.seen_log_json"
						:payments-json="invoice.payments_json"
						type-name="Invoice"
						:editing-log-json="invoice.editing_log_json">
					</activity-log>

					<revision-log
						:invoice-revisions="revisions"
						v-on:restoreClick="restoreRevision">		
					</revision-log>	

				</section>
				<div class="col-lg-6">
					<table v-if="!('is_finalized' in invoice) || invoice.is_finalized == 0" class="table table-striped table-sm totals-table text-right">
						<thead>
							<tr>
								<td></td>
								<th class="text-center">Current Billing</th>
								<td class="text-center">+</td>
								<th class="text-center">Previous Billing</th>
								<td class="text-center">=</td>
								<th class="text-center">Billed to Date</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="line in invoiceTotals()" :style="{borderTop: 'topBorder' in line ? '1px solid #a0a0a0' : 'none'}">
								<th>{{ line.label }}</th>
								<td>{{ line.current.toCurrency() }}</td>
								<td></td>
								<td>{{ line.previous.toCurrency() }}</td>
								<td></td>
								<td>{{ line.total.toCurrency() }}</td>
							</tr>
						</tbody>
					</table>
					<div v-else>
						<!--
						<div v-if='"tag" in $route.params && $route.params.tag == "new"'>
							<div class="preview-message">
								The preview will be shown once you save the invoice for the first time

								<div>
									<button class="btn btn-primary" type="button" v-on:click="saveInvoice(false)">
										<i class="fa fa-save"></i>
										Save
									</button>
								</div>
							</div>
						</div>
						<div v-else>
						-->
							<a class="float-right" :href="invoicePreviewSrc" target="_blank">View in another tab</a>
							<h3>
								<!--<i v-if="!previewExpanded" class="fa fa-chevron-left icon-button" title="Expand Preview Panel" v-on:click="togglePreviewSize"></i>
								<i v-else class="fa fa-chevron-right icon-button" title="Retract Preview Panel" v-on:click="togglePreviewSize"></i>-->
								Preview
							</h3>

							<pdf-js :url="invoicePreviewSrc" :page="1"></pdf-js>
						<!--</div>-->
					</div>
				</div>
			</div>
		</template>
	</collapsible-panel>

	<modal-dialog :modal-visible="deleteConfirmModalVisible"
		title="Delete Invoice"
		v-on:closeModal="deleteConfirmModalVisible = false"
		v-on:confirm="confirmDeleteInvoice"
		confirm-button-text="Yes, Delete"
		button-class="btn-outline-danger">

		Are you sure you want to delete this invoice?

	</modal-dialog>

	<modal-dialog
		:modal-visible="partialPaymentModal.visible"
		title="Make a Partial Payment"
		confirm-button-text="Make Payment"
		cancel-button-text="Cancel"
		:working="partialPaymentModal.working"
		v-on:closeModal="partialPaymentModal.visible = false"
		v-on:confirm="confirmPartialPayment">

		<field label="Amount of Partial Payment" 
			col-class="col-6"
			:params="{type:'number',step:0.01}"
			v-model="partialPaymentModal.payment_amount">
		</field>

	</modal-dialog>
</div>
</template>
<script type="text/javascript">
import PdfJs from "@/components/PdfJs"
import Field from "@/components/Field"
import Alert from "@/components/Alert"
import CollapsiblePanel from "@/layout_fragments/CollapsiblePanel"
import InvoiceDeliverySection from "@/components/super_components/InvoiceDeliverySection"
import ActivityLog from "@/components/ActivityLog"
import RevisionLog from "@/components/RevisionLog"
import ContextMenu from "@/layout_fragments/ContextMenu"
import ModalDialog from "@/components/ModalDialog"

import PanelMenu from "@/components/PanelMenu"

import InvoiceMixins from "@/mixins/InvoiceMixins.js"

export default {
	components: {
		PdfJs,
		Field,
		CollapsiblePanel,
		PanelMenu,
		Alert,
		InvoiceDeliverySection,
		ActivityLog,
		ContextMenu,
		RevisionLog,
		ModalDialog
	},
	mixins: [InvoiceMixins],
	props: ["invoiceSlug"],
	data() {
		return {
			drawPercentDecimals: 4,
			invoiceTasksQty: [],
			invoicedChangesQty: [],
			previewExpanded: false,
			previewRefresher: 0,
			invoice: {},
			clientInvoiceSettings: {},
			customer: {},
			tasks: [],
			changeOrders: [],
			revisions: [],
			activeStep: "Draw",
			deleteConfirmModalVisible: false,
			overbilledTasksAlert: true,
			overbilledChangesAlert: true,
			appliedTaxes: {},
			partialPaymentModal: {
				visible: false,
				payment_amount: 0,
				working: false
			},
		}
	},
	watch: {
		'$store.project.billing_progress_method'() {
			if ("id" in this.$store.project && this.tasks.length)
				this.repopulateDrawQtys()
		},
		'$store.project'() {
			if ("id" in this.$store.project) 
				this.fetchData()
		},
		'$store.company'() {
			this.setSalesTax()
		},
		invoiceSlug() {
			this.fetchData()
		}
	},
	computed: {
		progressUnits() {
			if (this.$store.project.billing_progress_method == 'unit')
				return 'units'
			else if (this.$store.project.billing_progress_method == 'dollars')
				return '$'
			else if (this.$store.project.billing_progress_method == 'percent')
				return '%'
		},
		drawQtyFieldParams() {
			let step = 0.01
			if (this.$store.project && this.$store.project.billing_progress_method == 'percent')
				step = 0.0001

			return {type: 'number', min: 0, step}
		},
		panelTabs() {
			let tabs = ['Draw','Finalizing']

			if (this.invoice.is_finalized == 1)
				tabs.push("Delivery")

			return tabs
		},
		hasOverbilledChanges() {

			let mutatedChanges = false
			if (this.$store.project.billing_progress_method == 'dollars')
				mutatedChanges = this.mutateChangeOrders(this.changeOrders)

			return this.invoicedChangesQty.find((changeQty,index) => {
				if (this.$store.project.billing_progress_method == 'percent' && changeQty > 100)
					return true 
				else if (this.$store.project.billing_progress_method == 'dollars') {
					if (changeQty > +mutatedChanges[index].pretax_total)
						return true
				}
				else if (this.$store.project.billing_progress_method == 'unit' && changeQty > 1)
					return true
			})
		},
		hasOverbilledTasks() {
			let mutatedTasks = false
			if (this.$store.project.billing_progress_method == 'dollars')
				mutatedTasks = this.mutateTasks(this.tasks)

			return this.invoiceTasksQty.find((taskQty,index) => {
				if (this.$store.project.billing_progress_method == 'percent' && taskQty > 100)
					return true
				else if (this.$store.project.billing_progress_method == 'dollars') {
					if (taskQty > +mutatedTasks[index].qty * +mutatedTasks[index].unit_value)
						return true
				}
				else if (this.$store.project.billing_progress_method == 'unit' && taskQty > +this.tasks[index].qty)
					return true
			})
		},
		isLatestJobInvoice() {
			if (this.invoiceSlug == "new" || !this.invoice || this.invoice.invoice_num === undefined || this.invoice.invoice_num === null)
				return false

			let maxInvoiceNum = +this.invoice.invoice_num
			this.$store.invoices.forEach((projectInvoice) => {
				if (+projectInvoice.invoice_num > maxInvoiceNum)
					maxInvoiceNum = +projectInvoice.invoice_num
			})

			return +this.invoice.invoice_num === maxInvoiceNum
		},
		previousTotalHoldBackReleases() {
			let total = this.$store.invoices.reduce((acc,inv) => {
				let holdbackRelease = 0
				if (inv.holdback_release)
					holdbackRelease = +inv.holdback_release

				return acc + +holdbackRelease
			},0)

			return total
		},
		contextMenuItems() {
			let items = []

			if (this.invoice.is_finalized == 1) {

				if (this.invoice.is_processed == 0) {
					items.push({
						icon: 'fa-archive',
						text: "Mark as Processed",
						clickFunction: "toggleProcessed",
						arg: true
					})
				}
				else {
					items.push({
						icon: 'fa-edit',
						text: "Mark as Un-Processed",
						clickFunction: "toggleProcessed",
						arg: false
					})	
				}

				if (this.invoice.is_paid == 0) {
					items.push({
						icon: 'fa-dollar-sign',
						text: "Mark as Paid",
						clickFunction: "recordFullPayment",
						arg: true
					})
				}
				else {
					items.push({
						icon: 'fa-dollar-sign',
						text: "Revert Payment",
						clickFunction: "revertPayment",
						arg: false
					})
				}
				
				items.push({
					icon: 'fa-dollar-sign',
					text: "Partial Payment",
					clickFunction: "makePartialPayment"
				})
				
				//check for invoices ahead of it
				let invoicesAhead = this.$store.invoices.find(inv => +inv.invoice_num > +this.invoice.invoice_num && inv.is_finalized == 1)

				let disabled = false
				if (invoicesAhead)
					disabled = true

				items.push({
					icon: 'fa-lock-open',
					text: "De-finalize for Editing",
					clickFunction: "definalize",
					disabled
				})
			}
			else {
				if (this.isLatestJobInvoice) {
					items.push({
						icon: 'fa-trash',
						text: 'Delete Invoice',
						clickFunction: "deleteInvoice",
						itemClass: "text-danger"
					})
				}
			}

			return items
		},
		invoicePreviewSrc() {
			return window.apiBase+"JobInvoice/pdf?slug="+this.invoiceSlug+"&token="+this.$store.sessionToken+"&humanReadable=true"+"&v="+this.previewRefresher
		}
	},
	methods: {
		activateStep(step) {
			this.activeStep = step
			this.setSalesTax()
		},
		togglePreviewSize() {
			this.previewExpanded = !this.previewExpanded
			this.previewRefresher++
		},
		closeInvoice() {
			this.$emit("closeInvoice")
		},
		setSalesTax() {
			let vm = this
			this.appliedTaxes = {}

			let appliedSalesTaxObj = {}
			try {
				appliedSalesTaxObj = JSON.parse(this.invoice.applied_sales_tax_json)
			}
			catch (e) {}

			this.salesTaxes.forEach(tax => {
				if (tax.label in appliedSalesTaxObj)
					vm.appliedTaxes[tax.label] = 1
			})
		},
		makePartialPayment() {
			this.partialPaymentModal.visible = true
			this.partialPaymentModal.payment_amount = 0
		},
		confirmPartialPayment() {
			let vm = this

			let payload = {
				id: this.invoice.id,
				payment_amount: this.partialPaymentModal.payment_amount
			}

			this.partialPaymentModal.working = true

			this.postData("JobInvoice/partial-payment",payload,true).then(response => {
				vm.partialPaymentModal.working = false
				if (response.success) {
					vm.partialPaymentModal.visible = false
					vm.alertSuccess("Partial payment recorded")
					vm.previewRefresher++
					vm.fetchInvoice(this.invoiceSlug)
				}
			})
		},
		formatDrawProgressUnits(record) {
			if (this.$store.project.billing_progress_method == 'percent')
				return +record.completedPercentRaw.round(this.drawPercentDecimals) + '%'
			else if (this.$store.project.billing_progress_method == 'dollars')
				return "$ " + record.completedDollars

			return +record.completedQtyRaw.round(1)
		},
		formatDrawQtyDisplay(qty) {
			if (this.$store.project.billing_progress_method == 'percent')
				return +qty.round(this.drawPercentDecimals)
			else if (this.$store.project.billing_progress_method == 'dollars')
				return qty.toCurrency()

			return qty
		},
		convertTaskQtys(billingProgressMethod) {
			let vm = this

			if (billingProgressMethod == 'dollars')
				return true

			this.invoiceTasksQty.forEach((value,index) => {
				if (billingProgressMethod == 'percent') {
					//convert qty from unit to percent
					let percent = +vm.invoiceTasksQty[index] / +vm.tasks[index].qty * 100
					vm.invoiceTasksQty[index] = percent.round(vm.drawPercentDecimals)
				}
				else {
					//convert qty from percent to unit
					let units = +vm.invoiceTasksQty[index] * +vm.tasks[index].qty / 100
					vm.invoiceTasksQty[index] = units.round(5)
				}
			})

			this.invoicedChangesQty.forEach((value,index) => {
				if (billingProgressMethod == 'percent') {
					//convert qty from unit to percent
					let percent = +vm.invoicedChangesQty[index] * 100
					vm.invoicedChangesQty[index] = percent.round(vm.drawPercentDecimals)
				}
				else {
					//convert qty from percent to unit
					let units = +vm.invoicedChangesQty[index] / 100
					vm.invoicedChangesQty[index] = units.round(5)
				}
			})
		},
		contextMenuClick(clickFunction,item) {
			if ("arg" in item)
				this[clickFunction](item.arg)
			else
				this[clickFunction]()
		},
		invoiceTotals() {
			let vm = this

			let totals = {
				grossInvoiceBase: {
					label: "Gross Invoice - Base Contract",
					current: 0,
					previous: 0,
					total: 0
				},
				grossInvoiceChanges: {
					label: "Gross Invoice - Changes",
					current: 0,
					previous: 0,
					total: 0
				},
				subtotalGrossInvoice: {
					label: "Subtotal - Gross Invoice",
					current: 0,
					previous: 0,
					total: 0
				},
				holdback: {
					label: "Less Holdback",
					current: 0,
					previous: 0,
					total: 0
				},
				holdbackRelease: {
					label: "Holdback Release",
					current: 0,
					previous: this.previousTotalHoldBackReleases,
					total: 0
				},
				netInvoice: {
					label: "Net Invoice",
					current: 0,
					previous: 0,
					total: 0,
					topBorder: true
				},
				taxes: {
					label: "Taxes",
					current: 0,
					previous: 0,
					total: 0
				},
				total: {
					label: "Total",
					current: 0,
					previous: 0,
					total: 0,
					topBorder: true
				}
			}

			let method = vm.$store.project.billing_progress_method

			if (method == 'percent')
				vm.convertTaskQtys('unit')

			let taxRate = this.getTotalTaxRate(this.$store.company.CompanySetting,vm.invoice.applied_sales_tax_json)

			let highestInvoiceNum = 0

			let prevInvoice = false

			if (this.invoiceSlug == 'new') {
				//find the highest invoice num, becuase that is the previous
				let highest = false;
				this.$store.invoices.forEach(invoice => {
					if (!highest)
						highest = invoice

					else if (+invoice.invoice_num > +highest.invoice_num)
						highest = invoice
				})

				prevInvoice = highest
			}
			else
				prevInvoice = this.$store.invoices.find(invoice => this.invoice.invoice_num-1 == invoice.invoice_num)

			if (prevInvoice) {
				totals.holdbackRelease.previous = this.previousTotalHoldBackReleases

				if (prevInvoice.JobInvoiceTask) {
					prevInvoice.JobInvoiceTask.forEach(task => {
						let taskFromList = vm.tasks.find(fromList => fromList.id == task.task_id)

						if (taskFromList) {
							if (vm.$store.project.billing_progress_method == 'dollars')
								totals.grossInvoiceBase.previous += +task.qty
							else
								totals.grossInvoiceBase.previous += +task.qty * +taskFromList.unit_value
						}

						let changeFromList = vm.changeOrders.find(fromList => fromList.id == task.quote_id)

						if (changeFromList) {
							if (vm.$store.project.billing_progress_method == 'dollars')
								totals.grossInvoiceChanges.previous += +task.qty
							else
								totals.grossInvoiceChanges.previous += +task.qty * +changeFromList.pretax_total
						}
					})
				}
			}

			this.tasks.forEach((task,index) => {
				let taskTotal = +vm.invoiceTasksQty[index]

				if (vm.$store.project.billing_progress_method != 'dollars')
					taskTotal = taskTotal * +task.unit_value

				if (!isNaN(taskTotal))
					totals.grossInvoiceBase.total += taskTotal
			})

			this.changeOrders.forEach((change,index) => {
				let changeTotal = +vm.invoicedChangesQty[index]

				if (vm.$store.project.billing_progress_method != 'dollars')
					changeTotal = changeTotal * +change.pretax_total

				if (!isNaN(changeTotal))
					totals.grossInvoiceChanges.total += changeTotal
			})

			let holdbackRelease = 0
			if (vm.invoice.holdback_release)
				holdbackRelease = +vm.invoice.holdback_release

			totals.holdbackRelease.total = holdbackRelease + +totals.holdbackRelease.previous

			let cols = ["current","previous","total"]
			cols.forEach(key => {
				totals.subtotalGrossInvoice[key] = totals.grossInvoiceBase[key] + totals.grossInvoiceChanges[key]
				totals.holdback[key] = +vm.$store.project.holdback_percentage * totals.subtotalGrossInvoice[key]

				totals.netInvoice[key] = totals.subtotalGrossInvoice[key] - totals.holdback[key] + +totals.holdbackRelease[key]

				totals.taxes[key] = totals.netInvoice[key] * taxRate
				totals.total[key] = totals.taxes[key] + totals.netInvoice[key]
			})

			Object.keys(totals).forEach(lineKey => {
				totals[lineKey].current = totals[lineKey].total - totals[lineKey].previous
			})

			if (method == 'percent')
				vm.convertTaskQtys('percent')

			return totals
		},
		definalize() {
			let vm = this

			let payload = {
				id: this.invoice.id,
				is_finalized: 0
			}

			this.postData("JobInvoice/save",payload,true).then(response => {
				if (response.success) {
					vm.invoice.is_finalized = 0
					vm.fetchInvoice(vm.invoice.slug)
					vm.alertSuccess("Invoice de-finalized")
					vm.activeStep = "Finalizing"
				}
			})
		},
		toggleProcessed(state) {
			let vm = this

			let payload = {
				id: this.invoice.id
			}

			if (state)
				payload.is_processed = 1
			else
				payload.is_processed = 0

			this.postData("JobInvoice/save",payload,true).then(response => {

				if (response.success) {

					if (state) 
						vm.alertSuccess("Invoice marked as Processed")
					
					else 
						vm.alertSuccess("Invoice marked as Un-Processed")

					vm.fetchInvoice(vm.invoice.slug)
				}
				
			})
		},
		recordFullPayment() {
			let vm = this

			let payload = {
				id: this.invoice.id,
				payment_amount: this.invoice.grand_total
			}

			this.postData("JobInvoice/partial-payment",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Invoice recorded as paid in full")
					vm.invoice.is_paid = 1
					vm.invoice.amount_paid = vm.invoice.grand_total
				}
			})
		},
		revertPayment() {
			let vm = this

			let payload = {
				id: this.invoice.id,
				payment_amount: -this.invoice.grand_total
			}

			this.postData("JobInvoice/partial-payment",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Negative payment recorded")
					vm.invoice.is_paid = 0
					vm.invoice.amount_paid = 0
				}
			})
		},
		/*
		togglePaid(state) {
			let vm = this

			let payload = {
				id: this.invoice.id
			}

			if (state)
				payload.is_paid = 1
			else
				payload.is_paid = 0

			this.postData("JobInvoice/save",payload,true).then(response => {
				
				if (response.success) {
					vm.invoice.is_paid = payload.is_paid

					if (state) 
						vm.alertSuccess("Invoice marked as Paid")
					
					else 
						vm.alertSuccess("Invoice marked as Un-Paid")
				}
				
			})
		},
		*/
		restoreRevision(invoiceRevision) {
			let vm = this

			let params = {
				id: invoiceRevision.id,
				revision_num: invoiceRevision.revision_num
			}

			this.postData("JobInvoice/activate-revision",params,true).then(response => {
				if (response.success) 
					vm.$router.push("/project/"+this.$store.project.slug+"/billing/"+response.data.slug)
			})
		},
		deleteInvoice() {
			this.deleteConfirmModalVisible = true
		},
		confirmDeleteInvoice() {
			let vm = this

			let payload = {
				id: this.invoice.id
			}

			this.postData("JobInvoice/delete",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Invoice deleted")
					vm.deleteConfirmModalVisible = false
					vm.$emit("closeInvoice"),
					vm.$emit("invoiceDeleted")
				}
			})
		},
		salesTaxToggle(taxKey) {
			let vm = this

			let appliedSalesTaxObj = {}
			let companySalesTax = {}
			
			this.$nextTick(() => {

				try {
					appliedSalesTaxObj = JSON.parse(this.invoice.applied_sales_tax_json)
				}
				catch (e) {}

				try {
					companySalesTax = JSON.parse(this.$store.company.CompanySetting.taxes_json)
				}
				catch (e) {}

				if (!vm.appliedTaxes[taxKey]) 
					delete appliedSalesTaxObj[taxKey]
				else if (taxKey in companySalesTax) 
					appliedSalesTaxObj[taxKey] = companySalesTax[taxKey]

				vm.invoice.applied_sales_tax_json = JSON.stringify(appliedSalesTaxObj)

				vm.$forceUpdate()

				if ("id" in vm.invoice) {
					let payload = {
						id: vm.invoice.id,
						applied_sales_tax_json: vm.invoice.applied_sales_tax_json
					}

					vm.postData("JobInvoice/save",payload,true)
				}
			})
		},
		saveInvoice(finalize) {
			let vm = this

			let payload = {
				job_id: this.$store.project.id,
				is_finalized: finalize ? 1 : 0,
			}

			let invoiceFields = [
				"holdback_release",
				"issue_date",
				"payment_terms",
				"attn",
				"customer_notes",
				"bottom_text",
				"applied_sales_tax_json"
			]

			invoiceFields.forEach(key => {
				payload[key] = vm.invoice[key]
			})

			//payload.applied_sales_tax_json = JSON.stringify(this.appliedTaxes)

			if ("id" in this.invoice)
				payload.id = this.invoice.id

			//calculate due date
			let dueInDays = payload.payment_terms

			let dueDate = this.moment(payload.issue_date).add(dueInDays,"days")

			payload.due_date = dueDate.format("YYYY-MM-DD")

			this.postData("JobInvoice/save",payload,true).then(response => {
				if (response.success) {
		
					let records = []
					vm.invoiceTasksQty.forEach((taskQty,index) => {
						if (taskQty != 0) {
							let task = vm.tasks[index]

							let units = +taskQty
							if (vm.$store.project.billing_progress_method == 'percent') 
								//we need to convert to units
								units = (+taskQty * +task.qty / 100).round(vm.drawPercentDecimals)

							let payload = {
								invoice_id: response.data.record_id,
								task_id: task.id,
								qty: units
							}

							records.push(payload)
						}
					})

					vm.invoicedChangesQty.forEach((changeQty,index) => {
						let units = 0
						if (changeQty != 0) {
							let change = vm.changeOrders[index]
							units = +changeQty
							if (vm.$store.project.billing_progress_method == 'percent') 
								//we need to convert to units
								units = (+changeQty / 100).round(vm.drawPercentDecimals)
						}

						let payload = {
							invoice_id: response.data.record_id,
							quote_id: vm.changeOrders[index].id,
							qty: units
						}

						records.push(payload)
					})

					let taskPayload = {
						records: JSON.stringify(records)
					}

					let promises = [
						vm.postData("JobInvoiceTask/save-batch",taskPayload,true)					
					]

					Promise.all(promises).then(responses => {
						if (responses[0].success && finalize) {
							vm.alertSaved("Invoice finalized")
							
							vm.invoice.is_finalized = 1

							vm.activeStep = 'Delivery'
						}
						else if (responses[0].success)
							vm.alertSaved("Invoice saved")

						if (vm.invoiceSlug == 'new') {
							//vm.$router.push("/project/"+this.$store.project.slug+"/billing/"+response.data.slug)
							window.history.replaceState({},"","/project/"+this.$store.project.slug+"/billing/"+response.data.slug)

							vm.$emit("invoiceGenerated",response.data.slug)
						}
					})
				}
			})
		},
		fetchInvoice(slug) {
			let vm = this

			let params = {
				include: [{
					model: "JobInvoiceTask",
					include: ["JobTask","JobChangeOrder"]
				}]
			}

			return new Promise((resolve,reject) => {

				vm.getJSON("JobInvoice/find/"+slug,params).then(response => {
					if (response.success) {
						vm.invoice = response.data

						vm.fetchInvoiceRevisions()

						vm.setSalesTax()
					}
					
					resolve(response)
				})

			})
		},
		fetchInvoiceRevisions() {
			let vm = this

			let params = {
				filters: [
					["job_id",this.$store.project.id],
					["invoice_num",this.invoice.invoice_num],
					["is_active_version",0]
				]
			}

			this.getJSON("JobInvoice/get",params,true).then(response => {
				if (response.success)
					vm.revisions = response.data
			})
		},
		repopulateDrawQtys() {
			let vm = this
			let method = vm.$store.project.billing_progress_method

			// Compl. to Date: this invoice's cumulative qty; for a new invoice, start from prior (highest)
			let complSourceInvoice = false
			if (vm.invoiceSlug != 'new' && vm.invoice.invoice_num != null) {
				complSourceInvoice = vm.invoice
				if (!complSourceInvoice.JobInvoiceTask)
					complSourceInvoice = vm.$store.invoices.find(inv => +inv.invoice_num == +vm.invoice.invoice_num)
			}
			else
				complSourceInvoice = vm.getHighestJobInvoice()

			vm.tasks.forEach((task, index) => {
				let totalQtyInvoiced = vm.getInvoiceTaskQtyForTask(complSourceInvoice, task.id)

				if (method == 'percent') {
					let completedPercent = 0
					if (task.qty != 0 && totalQtyInvoiced > 0)
						completedPercent = totalQtyInvoiced / +task.qty * 100
					vm.invoiceTasksQty[index] = completedPercent
				}
				else if (method == 'dollars')
					vm.invoiceTasksQty[index] = totalQtyInvoiced
				else
					vm.invoiceTasksQty[index] = totalQtyInvoiced
			})

			vm.changeOrders.forEach((change, index) => {
				let totalQtyInvoiced = vm.getInvoiceTaskQtyForChange(complSourceInvoice, change.id)

				if (method == 'percent') {
					let completedPercent = 0
					if (totalQtyInvoiced > 0)
						completedPercent = totalQtyInvoiced * 100
					vm.invoicedChangesQty[index] = completedPercent
				}
				else if (method == 'dollars')
					vm.invoicedChangesQty[index] = totalQtyInvoiced
				else
					vm.invoicedChangesQty[index] = totalQtyInvoiced
			})
		},
		fetchData() {
			let vm = this

			this.spinner(true)

			let promises = [
				this.getJSON("Contact/find/"+vm.$store.project.customer_id,{include: ["Contact"]}),
				this.fetchProjectTasks(false),
				this.fetchProjectChangeOrders(false)
			]

			if (this.invoiceSlug && this.invoiceSlug != 'new')
				promises.push(this.fetchInvoice(this.invoiceSlug))

			if (this.$store.invoices.length == 0)
				promises.push(this.fetchProjectInvoices(true,["JobInvoiceTask","JobChangeOrder"]))

			Promise.all(promises).then(responses => {
				vm.spinner(false)

				if (responses[0].success) {
					vm.clientInvoiceSettings = {}
					try {
						vm.clientInvoiceSettings = JSON.parse(responses[0].data.settings_json)
					}
					catch (ex) {}

					vm.customer = responses[0].data
				}

				if (responses[1].success)
					vm.repopulateDrawQtys()

				vm.setSalesTax()

				if (vm.invoice.is_finalized == 1)
					vm.activeStep = "Delivery"
				else
					vm.activeStep = "Draw"

				vm.$nextTick(() => {
					vm.$forceUpdate()
				})
				
			})
		}
	},
	created() {
		let vm = this

		if ("id" in this.$store.project) 
			this.fetchData()

		if ("tag" in this.$route.params && this.$route.params.tag == 'new') {
			if (this.$store.company && "CompanySetting" in this.$store.company) {
				this.invoice.bottom_text = this.$store.company.CompanySetting.invoice_notes

				this.invoice.applied_sales_tax_json = this.compileDefaultTaxes()
				this.setSalesTax()
			}
		}
	}
}
</script>

<style type="text/css" scoped>
	.totals-table th,
	.totals-table td {
		border: none;
	}

	.preview-message {
		text-align: center;
		padding: 40px;
		border: 1px solid rgba(0,0,0,.05);
		font-size: 16pt;
	}
</style>